import { useEffect, useState } from "react";
import { Bell, Plus, Send, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, C, errorMessage } from "../../lib/api";
import { Card, fmtWhen, inputCls } from "./ui";

// Ticker messages shown at the top of every table room (events, MTT announcements, congratulations…).
export const AnnouncementsPanel = () => {
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(C.ApiRoutes.ADMIN_ANNOUNCEMENTS).then((r) => setDoc(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!doc) return null;
  const msgs = doc.messages;
  const update = (i, patch) => setDoc({ ...doc, messages: msgs.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put(C.ApiRoutes.ADMIN_ANNOUNCEMENTS, { messages: msgs.filter((m) => m.text.trim()) });
      setDoc(data); toast.success("Ticker updated — tables pick it up within a minute");
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Table ticker · announcements" testId="announcements-panel">
      <p className="text-xs text-slate-400 mb-4">Active messages scroll across the top of every table room. Up to 20 messages, 200 characters each.</p>
      <button data-testid="jackpot-demo" onClick={() => api.post(C.ApiRoutes.ADMIN_JACKPOT_DEMO).then(() => toast.success("Jackpot preview sent to every open room — open a cash table to see the banner and ticker")).catch((e) => toast.error(errorMessage(e)))}
        className="mb-4 rounded-full px-4 py-1.5 text-xs border border-amber-400/40 text-amber-200 hover:bg-amber-400/10 flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Preview a jackpot announcement (nothing is paid)</button>
      <div className="flex flex-col gap-2">
        {msgs.map((m, i) => (
          <div key={i} className="flex items-center gap-2" data-testid={`announcement-${i}`}>
            <label className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 cursor-pointer select-none">
              <input type="checkbox" data-testid={`announcement-${i}-active`} checked={m.active} onChange={(e) => update(i, { active: e.target.checked })} className="accent-amber-500" /> On
            </label>
            <input data-testid={`announcement-${i}-text`} value={m.text} maxLength={200} onChange={(e) => update(i, { text: e.target.value })} className={`${inputCls} flex-1`} placeholder="Announcement text…" />
            <button data-testid={`announcement-${i}-delete`} onClick={() => setDoc({ ...doc, messages: msgs.filter((_, j) => j !== i) })} className="p-2 rounded-md text-slate-400 hover:text-red-300 hover:bg-white/5" title="Remove"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4">
        <button data-testid="announcement-add" onClick={() => msgs.length < 20 && setDoc({ ...doc, messages: [...msgs, { text: "", active: true }] })} className="rounded-full px-4 py-2 text-xs bg-white/5 hover:bg-white/10 flex items-center gap-1.5 btn-lift"><Plus className="w-3.5 h-3.5" /> Add message</button>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-500">Last change: {fmtWhen(doc.updated_at)}</span>
          <button data-testid="announcements-save" onClick={save} disabled={busy} className="btn-gold rounded-full px-5 py-2 text-xs font-display disabled:opacity-50">{busy ? "Saving…" : "Save ticker"}</button>
        </div>
      </div>
    </Card>
  );
};

// Push a notification to every player's inbox (red dot on their MENU tab until read).
export const BroadcastPanel = () => {
  const [form, setForm] = useState({ title: "", body: "" });
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get(C.ApiRoutes.ADMIN_NOTIFICATIONS).then((r) => setSent(r.data)).catch(() => setSent([]));
  useEffect(() => { load(); }, []);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(C.ApiRoutes.ADMIN_NOTIFICATIONS, form);
      toast.success(`Sent to ${data.recipients} players`); setForm({ title: "", body: "" }); load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Player notifications" testId="broadcast-panel">
      <p className="text-xs text-slate-400 mb-4 flex items-center gap-1.5"><Bell className="w-3.5 h-3.5" /> Lands in every player's Notifications section (side menu) with an unread badge. Use it for events, MTT starts, congratulations.</p>
      <div className="flex flex-col gap-2">
        <input data-testid="broadcast-title" value={form.title} maxLength={80} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} placeholder="Title (e.g. Sunday MTT · 7pm AEST)" />
        <textarea data-testid="broadcast-body" value={form.body} maxLength={500} rows={3} onChange={(e) => setForm({ ...form, body: e.target.value })} className={`${inputCls} resize-none`} placeholder="Message…" />
        <div className="flex justify-end"><button data-testid="broadcast-send" onClick={send} disabled={busy || !form.title.trim() || !form.body.trim()} className="btn-gold rounded-full px-5 py-2 text-xs font-display flex items-center gap-1.5 disabled:opacity-50"><Send className="w-3.5 h-3.5" /> {busy ? "Sending…" : "Send to all players"}</button></div>
      </div>
      {sent && sent.length > 0 && (
        <ul className="mt-4 flex flex-col divide-y divide-white/5 text-xs" data-testid="broadcast-history">
          {sent.slice(0, 8).map((b) => <li key={b.broadcast_id} className="py-2 flex items-center justify-between gap-3"><span className="text-amber-50 truncate">{b.title}</span><span className="text-slate-500 shrink-0">{b.recipients} players · {fmtWhen(b.created_at)}</span></li>)}
        </ul>
      )}
    </Card>
  );
};
